import { assign, assertEvent, sendParent, setup } from 'xstate';
import type { BrowseSchedulesView } from '@/lib/gql/__generated__/graphql';
import type { ScheduleEvent, ScheduleDomainEvent } from './events';
import { browseSchedulesActor, publishScheduleActor } from './schedule.actors';

type ScheduleContext = { items: BrowseSchedulesView[]; error: string | null };
const scheduleSetup = setup({
  types: { context: {} as ScheduleContext, events: {} as ScheduleEvent },
  actors: { browseSchedulesActor, publishScheduleActor },
});

export const scheduleMachine = scheduleSetup.createMachine({
  id: 'schedule',
  initial: 'idle',
  context: { items: [], error: null },
  on: {
    'schedule.load-browse': { target: '.loading', actions: assign({ error: () => null }) },
    'schedule.publish': { target: '.publishing' },
  },
  states: {
    idle: {},
    loading: {
      invoke: {
        src: 'browseSchedulesActor',
        input: undefined,
        onDone: {
          target: 'ready',
          actions: assign(({ event }) => ({ items: event.output.browseSchedules, error: null })),
        },
        onError: { target: 'error', actions: assign(({ event }) => ({ error: String(event.error) })) },
      },
    },
    ready: {},
    error: {},
    publishing: {
      invoke: {
        src: 'publishScheduleActor',
        input: ({ event }) => {
          assertEvent(event, 'schedule.publish');
          return {
            routes: event.routes,
            directions: event.directions,
            stops: event.stops,
            scheduledArrivals: event.scheduledArrivals,
          };
        },
        onDone: [
          {
            guard: ({ event }) => event.output.response.publishSchedule.success,
            target: 'loading',
            actions: [
              assign({ error: () => null }),
              sendParent(
                ({ event }) =>
                  ({ type: 'schedule.published', scheduleId: event.output.scheduleId }) satisfies ScheduleDomainEvent,
              ),
              sendParent({
                type: 'ui.toast.push',
                toast: { title: 'Schedule published', description: 'The route board is refreshing.', tone: 'success' },
              }),
            ],
          },
          {
            target: 'loading',
            actions: [
              assign(({ event }) => ({
                error: event.output.response.publishSchedule.error?.message ?? 'The schedule could not be published.',
              })),
              sendParent(({ event }) => ({
                type: 'ui.toast.push',
                toast: {
                  title: 'Schedule not published',
                  description:
                    event.output.response.publishSchedule.error?.message ?? 'The schedule could not be published.',
                  tone: 'error',
                },
              })),
            ],
          },
        ],
        onError: {
          target: 'loading',
          actions: [
            assign(({ event }) => ({ error: String(event.error) })),
            sendParent(({ event }) => ({
              type: 'ui.toast.push',
              toast: { title: 'Schedule not published', description: String(event.error), tone: 'error' },
            })),
          ],
        },
      },
    },
  },
});
